// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/components/views/RegistrosView.jsx
import React, { useMemo, useState, useCallback } from 'react';
import Pagination from '../Pagination';
import { formatCurrency } from '../../utils/formatters';
import { calculateProductStatus } from '../../utils/productCalculations';
import ImportCSVModal from '../modals/ImportCSVModal';

const RegistrosView = ({ inventory, brands, openModal, selectedIds, onSelectAll, onSelectOne, onDeleteSelected, addProduct, bulkAddProducts }) => {
    const [filters, setFilters] = useState({ search: '', brand: 'all', category: 'all', status: 'all', expiryPeriod: 'all' });
    const [sort, setSort] = useState({ key: 'name', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    
    // Estado do Carrinho de Compras (PDV)
    const [cart, setCart] = useState([]);

    // RECUPERADO: Função para limpar todos os filtros
    const resetFilters = () => {
        setFilters({ search: '', brand: 'all', category: 'all', status: 'all', expiryPeriod: 'all' });
    };

    const filteredAndSortedInventory = useMemo(() => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const currentSemester = currentMonth < 6 ? 1 : 2;       

        let items = inventory
            .map(p => ({ 
                ...p, 
                status: calculateProductStatus(p), 
                balance: p.initialQty - p.soldQty 
            }))
            .filter(p => {
                const searchLower = filters.search.toLowerCase();
                const matchesSearch = p.name.toLowerCase().includes(searchLower) || (p.brand && p.brand.toLowerCase().includes(searchLower)) || (p.code && p.code.toLowerCase().includes(searchLower));
                const matchesBrand = filters.brand === 'all' || p.brand === filters.brand;
                const matchesCategory = filters.category === 'all' || p.category === filters.category;
                const matchesStatus = filters.status === 'all' || p.status === filters.status;
                
                let matchesExpiryPeriod = true;
                if (filters.expiryPeriod !== 'all' && p.expiry) {
                    const expDate = new Date(p.expiry);
                    if (!isNaN(expDate)) {
                        const expMonth = expDate.getMonth();
                        const expYear = expDate.getFullYear();
                        const expSemester = expMonth < 6 ? 1 : 2;

                        if (filters.expiryPeriod === 'this_month') {
                            matchesExpiryPeriod = (expMonth === currentMonth && expYear === currentYear);
                        } else if (filters.expiryPeriod === 'this_semester') {
                            matchesExpiryPeriod = (expSemester === currentSemester && expYear === currentYear);
                        } else if (filters.expiryPeriod === 'this_year') {
                            matchesExpiryPeriod = (expYear === currentYear);
                        } else if (filters.expiryPeriod === 'next_year') {
                            matchesExpiryPeriod = (expYear === currentYear + 1);
                        } else if (filters.expiryPeriod === 'in_two_years') {
                            matchesExpiryPeriod = (expYear === currentYear + 2);
                        }
                    }
                }

                return matchesSearch && matchesBrand && matchesCategory && matchesStatus && matchesExpiryPeriod;
            });
        
        items.sort((a, b) => {
            const valA = a[sort.key];
            const valB = b[sort.key];
            if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return items;
    }, [inventory, filters, sort]);

    const paginatedInventory = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAndSortedInventory.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAndSortedInventory, currentPage, itemsPerPage]);

    const categories = useMemo(() => [...new Set(inventory.map(p => p.category))], [inventory]);

    const handleSort = useCallback((key) => {
        setSort(prevSort => ({
            key,
            direction: prevSort.key === key && prevSort.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const handleItemsPerPageChange = useCallback((value) => {
        setItemsPerPage(value);
        setCurrentPage(1);
    }, []);

    const isAllSelectedOnPage = useMemo(() => {
        if (paginatedInventory.length === 0) return false;
        return paginatedInventory.every(p => selectedIds.includes(p.id));
    }, [paginatedInventory, selectedIds]);

    const getStatusClass = (status) => {
        switch (status) {
            case 'OK': return 'status-ok';
            case 'ESTOQUE BAIXO': return 'status-estoque-baixo';
            case 'VALIDADE PRÓXIMA': return 'status-validade-proxima';
            case 'FORA DE VALIDADE': return 'status-fora-de-validade';
            case 'ESGOTADO': return 'status-esgotado';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleAddToCart = (product) => {
        if (product.balance <= 0) {
            alert('Este produto está sem saldo no estoque e não pode ser vendido.');
            return;
        }
        setCart(prevCart => {
            const existing = prevCart.find(item => item.id === product.id);
            if (existing) {
                if (existing.sellQty >= product.balance) {
                    alert('Quantidade máxima disponível em estoque já atingida.');
                    return prevCart;
                }
                return prevCart.map(item => item.id === product.id ? { ...item, sellQty: item.sellQty + 1 } : item);
            }
            return [...prevCart, { ...product, sellQty: 1 }];
        });
    };

    const cartTotal = cart.reduce((acc, item) => acc + (item.sellQty * item.salePrice), 0);
    const cartItemCount = cart.reduce((acc, item) => acc + item.sellQty, 0);

    const handleCheckout = () => {
        openModal('registerSale', { 
            cartItems: cart,
            onSuccess: () => setCart([])
        });
    };

    return (
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB] relative">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Gerenciamento de Produtos</h2>
            
            <div className="flex flex-col lg:flex-row justify-between gap-4 mb-4">
                <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                    <button onClick={() => openModal('manageBrands')} className="bg-gray-200 text-gray-800 w-full sm:w-auto font-semibold py-2 px-4 rounded-lg shadow-sm text-sm hover:bg-gray-300">⚙️ Gerenciar Marcas</button>
                    <button onClick={() => setIsImportModalOpen(true)} className="custom-button w-full sm:w-auto font-semibold py-2 px-4 rounded-lg shadow-sm text-sm">+ Envie Seu Arquivo CSV</button>
                    <button onClick={() => openModal('addProduct')} className="custom-button w-full sm:w-auto font-semibold py-2 px-4 rounded-lg shadow-sm text-sm">+ Adicionar Produto</button>
                    <button onClick={() => openModal('registerSale')} className="custom-button w-full sm:w-auto font-semibold py-2 px-4 rounded-lg shadow-sm text-sm">✓ Venda Rápida</button>
                </div>
                
                <div className="w-full lg:w-auto">
                    <select 
                        value={filters.expiryPeriod} 
                        onChange={e => setFilters({...filters, expiryPeriod: e.target.value})} 
                        className="w-full lg:w-64 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD] text-sm font-semibold text-gray-700 shadow-sm"
                    >
                        <option value="all">🕒 Todos os Vencimentos</option>
                        <option value="this_month">Vencem este mês</option>
                        <option value="this_semester">Vencem este semestre</option>
                        <option value="this_year">Vencem este ano</option>
                        <option value="next_year">Vencem ano que vem</option>
                        <option value="in_two_years">Vencem daqui a 2 anos</option>
                    </select>
                </div>
            </div>

            {/* Grid de Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                <input type="text" value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} placeholder="🔎 Produto, Marca ou Código de Barras..." className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]"/>
                <select value={filters.brand} onChange={e => setFilters({...filters, brand: e.target.value})} className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]">
                    <option value="all">Todas as marcas</option>
                    {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
                <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]">
                    <option value="all">Todas as categorias</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]">
                    <option value="all">Todos os status</option>
                    <option value="OK">OK</option>
                    <option value="ESTOQUE BAIXO">Estoque Baixo</option>
                    <option value="VALIDADE PRÓXIMA">Validade Próxima</option>
                    <option value="FORA DE VALIDADE">Fora de Validade</option>
                    <option value="ESGOTADO">Esgotado</option>
                </select>
            </div>

            {/* RECUPERADO: Botão de Limpar Filtros */}
            <div className="flex justify-end mb-4">
                <button onClick={resetFilters} className="text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-2">
                    Limpar Filtros
                </button>
            </div>

            {selectedIds.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-blue-800">{selectedIds.length} {selectedIds.length > 1 ? 'itens selecionados' : 'item selecionado'}</span>
                    <button onClick={onDeleteSelected} className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-800">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Excluir Selecionados
                    </button>
                </div>
            )}
            
            {/* START: Mobile View (Card List) */}
            <div className="space-y-4 md:hidden">
                <div className="flex items-center justify-between p-2">
                    <label className="flex items-center text-sm font-medium text-gray-700">
                        <input 
                            type="checkbox"
                            className="h-4 w-4 mr-2 rounded border-gray-300 text-[#D7BFBD] focus:ring-[#D7BFBD]"
                            checked={isAllSelectedOnPage}
                            onChange={() => onSelectAll(paginatedInventory.map(p => p.id), isAllSelectedOnPage)}
                        />
                        Selecionar Todos
                    </label>
                </div>
                
                {paginatedInventory.map(p => (
                    <div key={p.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-start">
                            <div className="flex items-start gap-3">
                                <input 
                                    type="checkbox"
                                    className="h-4 w-4 mt-1 rounded border-gray-300 text-[#D7BFBD] focus:ring-[#D7BFBD]"
                                    checked={selectedIds.includes(p.id)}
                                    onChange={() => onSelectOne(p.id)}
                                />
                                <div>
                                    <h3 className="text-[14px] font-semibold text-gray-800">{p.name}</h3>
                                    {/* MANTIDO: Legenda com Categoria e Validade para Mobile */}
                                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                        {p.category} <span className="text-gray-300">|</span> 
                                        Val. {p.expiry ? new Date(p.expiry).toLocaleDateString('pt-BR') : 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-end gap-1 max-w-[120px]">
                                <button className="action-btn text-xl" onClick={() => handleAddToCart(p)} title="Adicionar ao Carrinho">🛒</button>
                                <button className="action-btn text-xl" onClick={() => openModal('editProduct', p)} title="Editar Produto">✏️</button>
                                <button className="action-btn text-xl" onClick={() => openModal('editPrice', p)} title="Editar Preço">💲</button>
                                <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: p.id, type: 'product', name: p.name })} title="Excluir Produto">🗑️</button>
                            </div>
                        </div>
                        <div className="pt-3 border-t">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(p.status)}`}>{p.status}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center text-sm">
                            <div>
                                <p className="font-semibold text-gray-500">Qtd.</p>
                                <p className="font-bold text-lg">{p.balance}</p>
                            </div>
                            <div>
                                <p className="font-semibold text-gray-500">Custo</p>
                                <p className="text-gray-700">{formatCurrency(p.cost)}</p>
                            </div>
                            <div>
                                <p className="font-semibold text-gray-500">Venda</p>
                                <p className="font-bold text-gray-800">{formatCurrency(p.salePrice)}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {/* END: Mobile View */}

            {/* START: Desktop View (Table) */}
            <div className="overflow-x-auto hidden md:block">
                <table className="w-full text-left">
                    <thead className="border-b-2 border-[#F2DFDB]">
                        <tr>
                            <th className="p-4 w-4">
                                <input 
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-[#D7BFBD] focus:ring-[#D7BFBD]"
                                    checked={isAllSelectedOnPage}
                                    onChange={() => onSelectAll(paginatedInventory.map(p => p.id), isAllSelectedOnPage)}
                                />
                            </th>
                            <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('name')}>Produto {sort.key === 'name' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                            <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('balance')}>Quantidade {sort.key === 'balance' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                            <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('cost')}>Custo (R$) {sort.key === 'cost' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                            <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('salePrice')}>Preço Venda (R$) {sort.key === 'salePrice' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                            <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('status')}>Status {sort.key === 'status' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                            <th className="p-4 text-sm font-semibold text-gray-700 text-center whitespace-nowrap w-[140px]">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedInventory.map(p => (
                            <tr key={p.id} className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                <td className="p-4">
                                    <input 
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-[#D7BFBD] focus:ring-[#D7BFBD]"
                                        checked={selectedIds.includes(p.id)}
                                        onChange={() => onSelectOne(p.id)}
                                    />
                                </td>
                                <td className="p-4 max-w-md">
                                    <div className="flex flex-col">
                                    <span className="font-medium text-gray-900 break-words line-clamp-2" title={p.name}>{p.name}</span>
                                    <span className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                        {new Date(p.expiry).toLocaleDateString('pt-BR')} <span>|</span>
                                        {p.brand}<span>|</span>
                                        {p.category}
                                        {p.code && (<> <span>|</span>
                                        <span className="font-medium tracking-normal">{p.code}</span></>)}
                                    </span>
                                    </div>
                                </td>
                                <td className="p-4 text-gray-800 font-medium">{p.balance}</td>
                                <td className="p-4 text-gray-600">{formatCurrency(p.cost)}</td>
                                <td className="p-4 text-gray-600 font-semibold">{formatCurrency(p.salePrice)}</td>
                                <td className="p-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(p.status)}`}>{p.status}</span></td>
                                <td className="p-4 text-center whitespace-nowrap w-[140px]">
                                    <div className="flex items-center justify-center gap-1">
                                        <button className="action-btn text-xl" onClick={() => handleAddToCart(p)} title="Adicionar ao Carrinho">🛒</button>
                                        <button className="action-btn text-xl" onClick={() => openModal('editProduct', p)} title="Editar Produto Completo">✏️</button>
                                        <button className="action-btn text-xl" onClick={() => openModal('editPrice', p)} title="Ajuste Rápido de Preço">💲</button>
                                        <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: p.id, type: 'product', name: p.name })} title="Excluir Produto">🗑️</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* END: Desktop View */}

            <Pagination 
                totalItems={filteredAndSortedInventory.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={handleItemsPerPageChange}
            />
            <ImportCSVModal 
                isOpen={isImportModalOpen} 
                onClose={() => setIsImportModalOpen(false)} 
                onSave={bulkAddProducts} 
            />

            {/* WIDGET FLUTUANTE DO CARRINHO DE COMPRAS */}
            {cart.length > 0 && (
                <div className="fixed bottom-6 right-6 bg-gray-900 text-white p-4 rounded-2xl shadow-2xl z-50 flex items-center gap-4 border border-gray-700 animate-fade-in-up">
                    <div>
                        <p className="font-bold text-lg">🛒 {cartItemCount} {cartItemCount > 1 ? 'itens' : 'item'}</p>
                        <p className="text-[#D7BFBD] font-semibold">{formatCurrency(cartTotal)}</p>
                    </div>
                    <button 
                        onClick={handleCheckout} 
                        className="bg-[#D7BFBD] text-gray-900 font-bold py-2 px-6 rounded-lg shadow hover:bg-[#c4a9a7] transition-colors"
                    >
                        Finalizar Venda
                    </button>
                    <button 
                        onClick={() => setCart([])} 
                        className="text-gray-400 hover:text-white transition-colors" 
                        title="Limpar Carrinho"
                    >
                        ✖
                    </button>
                </div>
            )}
        </div>
    );
};

export default RegistrosView;